import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as AttendedIcon,
  AttachMoney as FeeIcon,
} from '@mui/icons-material';
import {
  fetchEventRegistrationsList,
  registerForEvent,
  cancelRegistration,
  markAttended,
} from '../../slices/eventsSlice';
import { fetchStudents } from '../../slices/studentsSlice';

const STATUS_LABELS = {
  registered: 'مسجل',
  cancelled: 'ملغي',
  attended: 'حاضر',
  absent: 'غائب',
};

const RegistrationManagement = ({ eventId, event }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { registrations, registrationsLoading } = useSelector((state) => state.events);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '',
  });
  const { students } = useSelector((state) => state.students || { students: [] });
  
  // Ensure registrations and students are arrays, not null
  const safeRegistrations = registrations || [];
  const safeStudents = students || [];

  useEffect(() => {
    if (eventId) {
      dispatch(fetchEventRegistrationsList({ event: eventId }));
    }
  }, [eventId, dispatch]);

  useEffect(() => {
    if (dialogOpen && safeStudents.length === 0) {
      dispatch(fetchStudents({ is_archived: false }));
    }
  }, [dialogOpen, safeStudents.length, dispatch]);

  const handleOpenDialog = () => {
    setFormData({ student_id: '' });
    setDialogOpen(true);
  };

  const handleRegister = async () => {
    try {
      await dispatch(
        registerForEvent({
          event_id: eventId,
          student_id: formData.student_id,
        })
      ).unwrap();
      showToast('تم التسجيل بنجاح', 'success');
      setDialogOpen(false);
      dispatch(fetchEventRegistrationsList({ event: eventId }));
    } catch (error) {
      showToast(error?.message || 'حدث خطأ', 'error');
    }
  };

  const handleCancelRegistration = async (registrationId) => {
    if (window.confirm('هل أنت متأكد من إلغاء التسجيل؟')) {
      try {
        await dispatch(cancelRegistration(registrationId)).unwrap();
        showToast('تم إلغاء التسجيل بنجاح', 'success');
        dispatch(fetchEventRegistrationsList({ event: eventId }));
      } catch (error) {
        showToast(error?.message || 'حدث خطأ', 'error');
      }
    }
  };

  const handleMarkAttended = async (registrationId) => {
    try {
      await dispatch(markAttended(registrationId)).unwrap();
      showToast('تم تسجيل الحضور بنجاح', 'success');
      dispatch(fetchEventRegistrationsList({ event: eventId }));
    } catch (error) {
      showToast(error?.message || 'حدث خطأ', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'registered':
        return 'info';
      case 'attended':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'absent':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">
            التسجيلات ({safeRegistrations.length || 0})
            {event?.max_participants && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                / {event.max_participants}
              </Typography>
            )}
          </Typography>
          {event?.student_fee && parseFloat(event.student_fee) > 0 && (
            <Chip
              icon={<FeeIcon />}
              label={`رسوم الطالب: ${parseFloat(event.student_fee).toFixed(2)} ₪`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        {event?.requires_registration && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            disabled={event.max_participants && safeRegistrations.length >= event.max_participants}
          >
            تسجيل طالب
          </Button>
        )}
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>الطالب</TableCell>
              <TableCell>تاريخ التسجيل</TableCell>
              <TableCell>الحالة</TableCell>
              {event?.student_fee && parseFloat(event.student_fee) > 0 && (
                <TableCell>الرسوم</TableCell>
              )}
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {safeRegistrations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={event?.student_fee && parseFloat(event.student_fee) > 0 ? 5 : 4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    لا توجد تسجيلات
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              safeRegistrations.map((registration) => (
                <TableRow key={registration.id}>
                  <TableCell>
                    {registration.student_name || registration.parent_name || '-'}
                  </TableCell>
                  <TableCell>{formatDate(registration.registered_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[registration.status] || registration.status}
                      color={getStatusColor(registration.status)}
                      size="small"
                    />
                  </TableCell>
                  {event?.student_fee && parseFloat(event.student_fee) > 0 && (
                    <TableCell>
                      {registration.fee_applied ? (
                        <Chip
                          icon={<FeeIcon />}
                          label={
                            registration.fee_applied.is_paid
                              ? `مدفوع ${parseFloat(registration.fee_applied.amount).toFixed(2)} ₪`
                              : `${parseFloat(registration.fee_applied.amount).toFixed(2)} ₪`
                          }
                          color={registration.fee_applied.is_paid ? 'success' : 'warning'}
                          size="small"
                        />
                      ) : (
                        <Chip label="لم تُطبَّق" size="small" color="default" />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack useFlexGap direction="row" spacing={1}>
                      {registration.status === 'registered' && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleMarkAttended(registration.id)}
                          >
                            <AttendedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelRegistration(registration.id)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Register Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تسجيل طالب</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>الطالب *</InputLabel>
            <Select
              value={formData.student_id}
              label="الطالب *"
              onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
            >
              <MenuItem value="">اختر طالب</MenuItem>
              {safeStudents.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {student.first_name} {student.second_name || ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleRegister}
            variant="contained"
            disabled={!formData.student_id}
          >
            تسجيل
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationManagement;
